import { GridColDefVNW } from 'vnetwork-ui/dist/components/DataGridPro'
import CellDate from '@/components/GokuDataGrid/CellDate'
import CellStatus from '@/components/GokuDataGrid/CellStatus'
import { CustomerMailTask } from '@/modules/Customer/types'
import EyeIcons from '@/icons/EyeIcons'
import { IconButton } from 'vnetwork-ui'
import { useModal } from '@/contexts/modal'
import { ViewMetadataModalProps } from '@/modals/MailHistoryModals/ViewMetadata'
import { useTranslation } from 'react-i18next'
import CheckCircleOutlinedIcons from '@/icons/CheckCircleOutlinedIcons'

interface EmailHistoryColumnsOptions {
    onConfirmReceived?: (row: CustomerMailTask) => void
    confirmingTaskId?: number | null
}

export const useEmailHistoryColumns = ({
    onConfirmReceived,
    confirmingTaskId,
}: EmailHistoryColumnsOptions = {}): GridColDefVNW<CustomerMailTask>[] => {
    const { setModal } = useModal()
    const { t } = useTranslation('customer')

    return [
        {
            field: 'mailer_id',
            headerName: t('mailer.email-history.columns.id'),
            width: 80,
            renderCell: ({ row }) => row.mailer_id,
        },
        {
            field: 'mailer_subject',
            headerName: t('mailer.email-history.columns.subject'),
            width: 400,
            renderCell: ({ row }) => row.mailer_subject || '-',
        },
        {
            field: 'mailer_receiver',
            headerName: t('mailer.email-history.columns.recipients'),
            width: 250,
            renderCell: ({ row }) => row.mailer_receiver?.join(', ') || '-',
        },
        {
            field: 'mailer_sender',
            headerName: t('mailer.email-history.columns.sender'),
            width: 200,
            renderCell: ({ row }) => row.mailer_sender || '-',
        },
        {
            field: 'mailer_status',
            headerName: t('mailer.email-history.columns.status'),
            width: 160,
            renderCell: ({ row }) => {
                const isReceived = row.receipt_status === 'received'
                const status = isReceived ? 'received' : row.mailer_status
                const getStatusColor = (status: string) => {
                    switch (status) {
                        case 'received':
                            return 'success'
                        case 'sent':
                            return 'success'
                        case 'failed':
                            return 'error'
                        case 'pending':
                            return 'warning'
                        default:
                            return 'default'
                    }
                }

                const getStatusText = (status: string) => {
                    switch (status) {
                        case 'received':
                            return t('mailer.email-history.status.received')
                        case 'sent':
                            return t('mailer.email-history.status.sent')
                        case 'failed':
                            return t('mailer.email-history.status.failed')
                        case 'pending':
                            return t('mailer.email-history.status.pending')
                        default:
                            return status
                    }
                }

                return (
                    <div className="flex items-center gap-2">
                        <CellStatus
                            label={getStatusText(status)}
                            color={getStatusColor(status)}
                        />
                        {row.mailer_status === 'sent' && !isReceived && (
                            <IconButton
                                onClick={() => onConfirmReceived?.(row)}
                                size="small"
                                color="primary"
                                disabled={
                                    !onConfirmReceived ||
                                    confirmingTaskId === row.mailer_id
                                }
                                title={t('mailer.email-history.confirm.button')}
                            >
                                <CheckCircleOutlinedIcons className="w-4 h-4" />
                            </IconButton>
                        )}
                    </div>
                )
            },
        },
        {
            field: 'mailer_referrer_type',
            headerName: t('mailer.email-history.columns.type'),
            width: 180,
            renderCell: ({ row }) => {
                const type = row.mailer_referrer_type || ''
                const shortType = type.split('::').pop() || type
                return shortType
            },
        },
        {
            field: 'mailer_created_at',
            headerName: t('mailer.email-history.columns.created'),
            width: 180,
            renderCell: ({ row }) => <CellDate date={row.mailer_created_at} />,
        },
        {
            field: 'mailer_performed_at',
            headerName: t('mailer.email-history.columns.sent'),
            width: 180,
            renderCell: ({ row }) =>
                row.mailer_performed_at ? (
                    <CellDate date={row.mailer_performed_at} />
                ) : (
                    '-'
                ),
        },
        {
            field: 'metadata',
            headerName: t('mailer.email-history.columns.metadata'),
            width: 100,
            headerAlign: 'center',
            align: 'center',
            renderCell: ({ row }) => {
                const handleViewMetadata = () => {
                    setModal<ViewMetadataModalProps>(
                        'MailHistoryModals/ViewMetadata/index',
                        {
                            metadata: {
                                mailer_id: row.mailer_id,
                                mailer_sender: row.mailer_sender,
                                mailer_subject: row.mailer_subject,
                                mailer_receiver: row.mailer_receiver,
                                mailer_cc: row.mailer_cc,
                                mailer_bcc: row.mailer_bcc,
                                mailer_status: row.mailer_status,
                                mailer_referrer_type: row.mailer_referrer_type,
                                ...row.metadata,
                            },
                            taskId: row.mailer_id,
                        }
                    )
                }

                return (
                    <IconButton
                        onClick={handleViewMetadata}
                        size="small"
                        color="primary"
                    >
                        <EyeIcons className="w-4 h-4" />
                    </IconButton>
                )
            },
        },
    ]
}
